import ContainerLoading from "../components/ContainerLoading";
import {
  Button,
  Chip,
  Grid2,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { IconX, IconCheck, IconEdit } from "@tabler/icons-react";
import { IconButton, Tooltip } from "@mui/material";
import PageHeader from "../components/PageHeader";
import { useMemo, useState } from "react";
import { IconWallet } from "@tabler/icons-react";
import PayableRegister from "../components/PayableRegister";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Indicators,
  PayableMonth,
  Payables,
  payableUpadateRecurrence,
} from "../services/payable";
import { MRT_ColumnDef } from "material-react-table";
import { formatPrice } from "../helpers/format";
import VirtualizedTable from "../components/VirtualizeTable";
import { useUserStore } from "../stores/user";
import { toast } from "react-toastify";
import PayableEdit from "../components/PayableEdit";
import PayableOverDues from "../components/PayableOverDues";
import PayableRecurring from "../components/PayableRecurring";
import IndicatorsCard from "../components/IndicatorsCard";
import moment from "moment";

const PayableCounts = () => {
  const [value, setValue] = useState("1");
  const [open, setOpen] = useState(false);
  const { user } = useUserStore();
  const [editOpen, setEditOpen] = useState(false);
  const [selectedPayable, setSelectedPayable] = useState(null);

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    setValue(newValue);
  };

  const {
    data: dataPayables,
    isLoading: isLoadingPayables,
    isFetching: isFetchingPayables,
    refetch: refetchPayables,
  } = useQuery({
    queryKey: ["payables"],
    queryFn: async () => await Payables(),
    enabled: !!user?.storeId,
    initialData: {
      month: [],
      recurrence: [],
    },
  });

  const {
    data: dataIndicators,
    isLoading: isLoadingIndicators,
    isFetching: isFetchingIndicators,
    refetch: refetchIndicators,
  } = useQuery({
    queryKey: ["indicators", user?.storeId],
    queryFn: async () => await Indicators({ storeId: user?.storeId }),
    enabled: !!user?.storeId,
  });

  const { mutate: markAsPaidRecurrence } = useMutation({
    mutationFn: payableUpadateRecurrence,
    onSuccess: () => {
      toast.success("Conta marcada como paga!");
      refetchPayables();
    },
    onError: () => {
      toast.error("Erro ao marcar como paga.");
    },
  });

  const { mutate: cancelPayableRecurrence } = useMutation({
    mutationFn: payableUpadateRecurrence,
    onSuccess: () => {
      toast.success("Conta cancelada com sucesso.");
      refetchPayables();
    },
    onError: () => {
      toast.error("Erro ao cancelar conta.");
    },
  });

  const handleCancel = (id: number) => {
    const item = dataPayables.find((i: any) => i.id === id);
    if (!item) return;

    const storeId = Number(user?.storeId);

    cancelPayableRecurrence({
      storeId,
      id: item.id,
      status: "CANCELED",
    });
  };

  const handleMarkAsPaid = (id: number) => {
    const item = dataPayables.find((i: any) => i.id === id);
    if (!item) return;

    markAsPaidRecurrence({
      id: item.id,
      status: "PAID",
      paymentDate: new Date(),
    });
  };

  type PayableItem = Awaited<ReturnType<typeof PayableMonth>>["month"][0];

  const columns = useMemo<MRT_ColumnDef<PayableItem>[]>(() => {
    return [
      {
        id: "acoes",
        header: "Ações",
        size: 80,
        Cell: ({ row }) => {
          const status = row.original.status;

          if (status === "PAID" || status === "CANCELED") return null;

          return (
            <Stack direction="row" spacing={1} justifyContent="center">
              <Tooltip title="Cancelar">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleCancel(row.original.id)}
                >
                  <IconX size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Marcar como pago">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleMarkAsPaid(row.original.id)}
                >
                  <IconCheck size={16} />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 110,
        Cell: ({ cell }) => {
          const status = cell.getValue() as string;
          let color: any = "";
          let label = status;

          if (status === "PENDING") {
            color = "warning";
            label = "Pendente";
          }

          if (status === "PAID") {
            color = "success";
            label = "Pago";
          }

          if (status === "OVERDUE") {
            label = "Atrasado";
            color = " #d35400";
          }

          if (status === "SCHEDULED") {
            color = "gray";
            label = "Agendado";
          }

          if (status === "CANCELED") {
            color = "error";
            label = "Cancelado";
          }

          return (
            <Chip
              label={label}
              color={
                ["warning", "success", "error", "info"].includes(color)
                  ? color
                  : undefined
              }
              sx={{
                bgcolor: !["warning", "success", "error", "info"].includes(
                  color
                )
                  ? color
                  : undefined,
                color: "#fff",
                width: "100%",
              }}
            />
          );
        },
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
      {
        accessorKey: "title",
        header: "Conta",
        size: 210,
      },
      {
        accessorKey: "amount",
        header: "Valor",
        size: 80,
        Cell: ({ cell }) => formatPrice(cell.getValue() as number) || "--",
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
      {
        accessorKey: "dueDate",
        header: "Vencimento",
        size: 160,
        Cell: ({ cell }) => {
          const v = cell.getValue() as string | null;
          return v ? moment.utc(v).format("DD/MM/YYYY") : "--";
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: { textAlign: "center" },
        },
        muiTableHeadCellProps: {
          align: "center",
          sx: {
            textAlign: "center",
            overflow: "visible",
          },
        },
      },
      {
        accessorKey: "paymentDate",
        header: "Pagamento",
        size: 150,
        Cell: ({ cell }) => {
          const v = cell.getValue() as string | null;
          return v ? moment.utc(v).format("DD/MM/YYYY") : "--";
        },
        muiTableHeadCellProps: {
          align: "center",
          sx: {
            textAlign: "center",
            overflow: "visible",
          },
        },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
      {
        id: "editar",
        header: "Editar",
        size: 50,
        Cell: ({ row }) => {
          return (
            <Stack direction="row" spacing={1} justifyContent="center">
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedPayable(row.original);
                    setEditOpen(true);
                  }}
                >
                  <IconEdit size={16} />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
    ];
  }, [dataPayables]);

  const refetchAll = () => {
    refetchPayables();
    refetchIndicators();
  };

  return (
    <ContainerLoading
      loading={
        isLoadingPayables ||
        isFetchingPayables ||
        isLoadingIndicators ||
        isFetchingIndicators
      }
    >
      <Grid2 container spacing={2} padding={2}>
        <PageHeader
          title="Contas a pagar"
          tooltip="Gerencie aqui as despesas da academia, registre pagamentos e acompanhe contas pendentes ou vencidas."
          subtitle="Todas as informações financeiras em um só lugar."
          options={
            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: { xs: "flex-start", lg: "flex-end" },
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                sx={{ gap: 0.5 }}
                onClick={() => setOpen(true)}
              >
                <IconWallet size={18} />
                <Typography variant="body2">Registrar Conta</Typography>
              </Button>
            </Stack>
          }
        />

        <Grid2 size={12}>
          <IndicatorsCard
            data={[
              {
                id: 1,
                title: "Gastos Mês Atual",
                value: formatPrice(dataIndicators?.currentMonthTotal),
                icon: "icon_calendar_dollar",
                hasInfo:
                  "Valor total de todas as contas com vencimento neste mês.",
                size: { xs: 12, sm: 6, md: 6 },
              },
              {
                id: 2,
                title: "Previsão Próximo Mês",
                value: formatPrice(dataIndicators?.nextMonthTotal),
                icon: "icon_calendar_dollar",
                hasInfo:
                  "Valor total de contas já criadas ou agendadas para o próximo mês.",
                size: { xs: 12, sm: 6, md: 6 },
              },
            ]}
          />
        </Grid2>
        <Grid2 size={12}>
          <Tabs
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              marginBottom: 2,
            }}
            onChange={handleChangeValue}
            value={value}
            textColor="inherit"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Pendentes" value="1" />
            <Tab label="Pagos" value="2" />
            <Tab label="Recorrentes" value="3" />
          </Tabs>
        </Grid2>
        {open && (
          <PayableRegister
            open={open}
            onClose={() => setOpen(false)}
            refetch={refetchAll}
          />
        )}
        {editOpen && (
          <PayableEdit
            open={editOpen}
            onClose={() => setEditOpen(false)}
            refetch={refetchAll}
            payable={selectedPayable}
            isRecurring
            pendings
          />
        )}
        {value === "1" && (
          <Grid2 size={12}>
            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <VirtualizedTable
                  data={dataPayables}
                  columns={columns}
                  fetchNextPage={() => Promise.resolve()}
                  isLoading={isLoadingPayables || isFetchingPayables}
                />
              </Grid2>
            </Grid2>
          </Grid2>
        )}
        {value === "2" && <PayableOverDues />}
        {value === "3" && <PayableRecurring />}
      </Grid2>
    </ContainerLoading>
  );
};

export default PayableCounts;
